import Actions from 'actions'
import { NotifyPracticeReasons } from 'consts/giConsts'
import useRolePermissions from 'hooks/useRolePermissions'
import usePatientTags from 'hooks/usePatientTags'
import useSpecificMemberAttention from 'hooks/useSpecificMemberAttention'
import { getActiveNeedsAttentionNoteId } from 'logic/patientNotesLogic'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'
import { v4 as uuidv4 } from 'uuid'

import { NOTE_INPUT_ID } from './notesConsts'

/**
 * Reply and Resolve for the patient's open Needs Attention, from the Internal Notes panel (GR-10744).
 * Both go through the resolve endpoint, like the Copilot tab's Reply/Resolve: a reply is a resolve
 * with a note, which is the only path that notifies the practice's HI.
 * While a Needs Attention is open, a practice user's composer starts as a reply; removing the tag makes it a
 * regular note. An HI user starts with a regular note and replies only after Reply: their reply resolves their
 * own Needs Attention and emails the practice's HI.
 * An automation-raised Needs Attention offers Resolve only: no HI asked, so a reply would notify nobody.
 * A user who cannot write notes (e.g. HI + Admin) has no composer, so gets Resolve only.
 */
export default ({ notes }) => {
  const dispatch = useDispatch()
  const { permissions, isHiUser } = useRolePermissions()
  const { patient } = useSelector(state => state.patientsReducer)
  const { lastResult } = useSelector(state => state.practiceReducer.resolveNeedsAttention)
  const { needsAttentionTag, needsAttentionTagDetails } = usePatientTags({ patient })
  const { isAwaitingCurrentMembersAttention } = useSpecificMemberAttention({
    note: needsAttentionTagDetails?.note,
    selectedPracticeMembersIds: needsAttentionTagDetails?.selectedPracticeMembersIds
  })

  const canAct =
    !!needsAttentionTag && !!permissions.needsAttentionResolving && (isHiUser || isAwaitingCurrentMembersAttention)

  const activeNeedsAttentionNoteId = useMemo(
    () => (canAct ? getActiveNeedsAttentionNoteId(notes) : null),
    [canAct, notes]
  )

  // A new Needs Attention resets the reply mode even if it was changed for the previous one.
  const [isReplyModeOn, setIsReplyModeOn] = useState(!isHiUser)
  useEffect(() => {
    setIsReplyModeOn(!isHiUser)
  }, [needsAttentionTag?.id, isHiUser])

  const canReply =
    canAct && !!permissions.notes?.addNotes && needsAttentionTagDetails?.reason !== NotifyPracticeReasons.Automation
  const isReplying = canReply && isReplyModeOn

  // This panel's own requests in flight, by requestId, so the composer is busy only for the patient a
  // request was sent for, not for every patient while any resolve is pending.
  const [requestsInFlight, setRequestsInFlight] = useState({})
  const isSaving = Object.values(requestsInFlight).includes(patient?.id)
  useEffect(() => {
    if (!lastResult?.requestId) return
    setRequestsInFlight(current => {
      if (!(lastResult.requestId in current)) return current
      const { [lastResult.requestId]: _done, ...rest } = current
      return rest
    })
  }, [lastResult])

  const sendResolve = useCallback(
    ({ action, note }) => {
      const requestId = uuidv4()
      setRequestsInFlight(current => ({ ...current, [requestId]: patient?.id }))
      dispatch(Actions.resolveHiNeedsAttention({ action, note, patientId: patient?.id, requestId }))
      return requestId
    },
    [dispatch, patient?.id]
  )

  const trackingProps = useMemo(() => ({ patientId: patient?.id, source: 'Internal Notes' }), [patient?.id])

  const handleReplyClicked = useCallback(() => {
    trackEvent('Needs Attention - reply clicked', trackingProps)
    setIsReplyModeOn(true)
    document.getElementById(NOTE_INPUT_ID)?.focus()
  }, [trackingProps])

  const handleReplyTagRemoved = useCallback(() => {
    trackEvent('Needs Attention - reply tag removed', trackingProps)
    setIsReplyModeOn(false)
  }, [trackingProps])

  const handleResolveClicked = useCallback(() => {
    trackEvent('Needs Attention - resolved', trackingProps)
    sendResolve({ action: 'resolve', note: null })
  }, [sendResolve, trackingProps])

  // The composer clears its text only once its own reply landed: the reducer records each request's
  // outcome by requestId, so another resolve, or a tag removed by someone else, cannot stand in for it.
  const pendingReply = useRef(null)
  useEffect(() => {
    if (pendingReply.current && lastResult?.requestId === pendingReply.current.requestId) {
      pendingReply.current.onDone({ isSuccess: lastResult.status === 'success' })
      pendingReply.current = null
    }
  }, [lastResult])

  // A reply sent for one patient never reports into another patient's composer.
  useEffect(() => {
    pendingReply.current = null
  }, [patient?.id])

  const submitReply = useCallback(
    ({ note, onDone }) => {
      trackEvent('Needs Attention - reply saved', trackingProps)
      const requestId = sendResolve({ action: 'reply', note })
      pendingReply.current = { requestId, onDone }
    },
    [sendResolve, trackingProps]
  )

  return useMemo(
    () => ({
      activeNeedsAttentionNoteId,
      canReply,
      isReplying,
      isSaving,
      onReplyClicked: handleReplyClicked,
      onReplyTagRemoved: handleReplyTagRemoved,
      onResolveClicked: handleResolveClicked,
      submitReply
    }),
    [
      activeNeedsAttentionNoteId,
      canReply,
      isReplying,
      isSaving,
      handleReplyClicked,
      handleReplyTagRemoved,
      handleResolveClicked,
      submitReply
    ]
  )
}
